import React, { ForwardedRef, PropsWithRef, ReactElement, ReactNode, useMemo, useRef } from 'react';
import type { BaseRowData, DataProvider } from '@adv-frontend/data-provider';
import { ListState } from 'react-stately';
import { useListBox, useOption } from 'react-aria';
import { ListLayout } from '@react-stately/layout';

import { TestProps } from '../../internal/types/testTypes';
import { VirtualList, VirtualListProps } from '../List/VirtualList';
import { ListItemProps } from '../List/ListItem';
import { useVirtualizer } from '../List/VirtualList/utils';
import { IconSelectSkinny } from '../../icons';

/** Flow-based DataProvider для строк виртуального списка выбора. */
export type VirtualListBoxDataProvider<RowData extends BaseRowData> = DataProvider<RowData>;

type ItemProps = Partial<PropsWithRef<ListItemProps>> & { id: string; ref: ForwardedRef<HTMLLIElement> };

type VirtualListBoxItemState<RowData extends BaseRowData> = Parameters<VirtualListProps<RowData>['children']>[0] & {
    /** Загруженные данные строки для совместимости с прежним контрактом рендерера. */
    data?: RowData;
    /** Обновляются ли данные строки для совместимости с прежним контрактом рендерера. */
    isFetching: boolean;
};

function addCompatibilityFields<RowData extends BaseRowData>(
    itemState: Parameters<VirtualListProps<RowData>['children']>[0],
): VirtualListBoxItemState<RowData> {
    const data = itemState.isReady || itemState.isRefreshing ? itemState.current() : undefined;

    return Object.assign(() => itemState(), itemState, {
        data,
        isFetching: itemState.isPending,
    });
}

export interface VirtualListBoxProps<RowData extends BaseRowData>
    extends TestProps,
        Omit<VirtualListProps<RowData>, 'children' | 'dataProvider' | 'placeholder'> {
    /** DataProvider для строк списка. */
    dataProvider: VirtualListBoxDataProvider<RowData>;

    /**
     * Стейт для управления выбором по идентификаторам строк.
     * Данные для отображения строк берутся из провайдера.
     */
    state: ListState<BaseRowData>;

    /**
     * Функция-рендерер для каждой строки данных
     * @param itemState состояние строки flow-based dataProvider
     * @param props пропсы для ListItem
     * @returns отрендеренный элемент
     * @example ```tsx
     * (itemState, props) => <ListItem {...props}>{itemState.current().name}</ListItem>
     * ```
     */
    children: (itemState: VirtualListBoxItemState<RowData>, props: ItemProps) => ReactElement | null;

    /**
     * aria-label для списка
     */
    'aria-label'?: string;

    /**
     * Секция, которая будет отображаться, если список пуст
     * @example emptySection={<ListItem isDisabled>Ничего не найдено</ListItem>}
     */
    emptySection?: ReactNode;
}

interface ItemRendererProps<RowData extends BaseRowData>
    extends Pick<VirtualListBoxProps<RowData>, 'state'>,
        TestProps {
    /** Функция отрисовки загруженной строки. */
    renderer: VirtualListBoxProps<RowData>['children'];
    /** Состояние строки flow-based DataProvider. */
    itemState: Parameters<VirtualListProps<RowData>['children']>[0];
    /** Идентификатор строки для управления выбором. */
    id: string;
}

const ItemRenderer = function ItemRenderer<RowData extends BaseRowData>(props: ItemRendererProps<RowData>) {
    const { renderer, state, itemState, id, 'data-testid': dataTestId = 'ListBox' } = props;
    const ref = useRef(null);
    // TODO DIRECT-243913 Из-за небезопасного доступа по id тут крашится страница, убрать isVirtualized после обновления
    const { isSelected } = useOption({ key: id, isVirtualized: false }, state, ref);
    const itemProps = {
        ref,
        id,
        onClick: () => {
            state.selectionManager.toggleSelection(id);
        },
        iconRight: isSelected ? IconSelectSkinny : undefined,
        isDisabled: state.disabledKeys.has(id),
        'data-testid': `${dataTestId}.${id}`,
        role: 'option',
    } satisfies ItemProps;
    const compatibleItemState = addCompatibilityFields(itemState);

    return renderer(
        compatibleItemState,
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        itemProps,
    );
};

/**
 * Компонент выбора из списка с виртуализацией и поддержкой dataProvider
 * @example
 * ```tsx
 * const dataProvider = api.useDataProvider();
 * const rowsIds = useAsyncFlow(dataProvider.rowsIds)();
 * const state = useListState({ items: rowsIds.map(id => ({ id })) });
 *
 * return <VirtualListBox dataProvider={dataProvider} state={state}>
 *  {itemState => <ListItem>{itemState.current().name}</ListItem>}
 * </VirtualListBox>
 * ```
 */
export const VirtualListBox = function VirtualListBox<RowData extends BaseRowData>(
    props: VirtualListBoxProps<RowData>,
) {
    const { state, size, emptySection } = props;
    const ref = useRef(null);
    const virtualizer = useVirtualizer({ size });
    const layout = useMemo(() => new ListLayout<BaseRowData>(virtualizer), [virtualizer]);

    useListBox(
        {
            ...props,
            isVirtualized: true,
            keyboardDelegate: layout,
        },
        state,
        ref,
    );

    return (
        <VirtualList {...props} placeholder={emptySection} ref={ref}>
            {itemState => {
                const id = itemState.isError ? '' : itemState.current().id;

                return (
                    <ItemRenderer
                        renderer={props.children}
                        state={state}
                        itemState={itemState}
                        id={id}
                        data-testid={props['data-testid']}
                    />
                );
            }}
        </VirtualList>
    );
};
